import { useState } from 'react';
import Button from '../../../../../components/form-control/Button';
import SelectDropdown from '../../../../../components/form-control/SelectDropdown';
import { updateIntermediary } from '../../../../../api/requests';
import { POLICY_ISSUANCE_MODES } from '../../../../../resources/enums';
import { POLICY_ISSUANCE_MODES_WITH_DESCRIPTION } from '../../../../../resources/constants';
import { removeUnderscoresAndCapitalize, showToast } from '../../../../../utils/miscFunctions';
import { BrokerInsurerIntegration } from '..';
import { IIntermediaryDTO } from './CreateIntermediary';

interface EditIssuanceModeModalProps {
  intermediary: BrokerInsurerIntegration;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Lightweight editor that lets an insurer change a broker's policy issuance mode without
 * going through the full underwriter form. Sends the complete intermediary payload (all
 * existing fields preserved) so the PUT only changes the issuance mode + derived midReady.
 */
const EditIssuanceModeModal = ({ intermediary, onClose, onSuccess }: EditIssuanceModeModalProps): JSX.Element => {
  const [issuanceMode, setIssuanceMode] = useState<string>(intermediary?.issuanceMode ?? '');
  const [submitting, setSubmitting] = useState<boolean>(false);

  const issuanceModeOptions = Object.values(POLICY_ISSUANCE_MODES).map((mode) => ({
    name: POLICY_ISSUANCE_MODES_WITH_DESCRIPTION[mode]?.title || removeUnderscoresAndCapitalize(mode),
    value: mode,
  }));

  const handleSave = async () => {
    if (!issuanceMode) {
      showToast.error('Please select an issuance mode.');
      return;
    }

    const source = intermediary as any;
    const payload: IIntermediaryDTO = {
      apikey: source.apikey,
      branchCode: source.branchCode,
      brokerId: source.brokerId,
      brokerName: source.brokerName,
      insurerId: source.insurerId,
      insurerName: source.insurerName,
      intermediaryCode: source.intermediaryCode,
      comprehensiveCommissionRate: source.comprehensiveCommissionRate,
      thirdPartyCommissionRate: source.thirdPartyCommissionRate,
      type: source.type,
      insurerContactEmails: source.insurerContactEmails,
      issuanceMode: issuanceMode.toUpperCase(),
      midReady: issuanceMode.toUpperCase() === POLICY_ISSUANCE_MODES.MID_LIVE,
    };

    setSubmitting(true);
    try {
      const response: any = await updateIntermediary(intermediary.id, payload);
      showToast.success(response?.message || 'Issuance mode updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      showToast.error(error?.description ?? 'Failed to update issuance mode');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <p className="text-sm text-gray-600 dark:text-darkBg-mainText mb-4">
        Set the policy issuance mode for <span className="font-semibold">{intermediary?.brokerName}</span>.
      </p>

      <SelectDropdown
        name="issuanceMode"
        label="Issuance Mode"
        list={issuanceModeOptions}
        listSelectedValue={issuanceMode}
        onChange={(value: string) => setIssuanceMode(value)}
        placeholder="Select issuance mode"
        required
      />

      <div className="flex justify-end gap-3 mt-8">
        <Button label="Cancel" bgColor="secondary" onClick={onClose} disabled={submitting} />
        <Button label={submitting ? 'Saving...' : 'Save'} bgColor="primary" onClick={handleSave} disabled={submitting} loading={submitting} />
      </div>
    </div>
  );
};

export default EditIssuanceModeModal;
